import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";

const UPDATED = "1 June 2026";
const CONTACT = "privacy@pitchcraft.app";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: [
      "Pitch Craft (\"we\", \"us\") provides an AI-powered sales training platform where you practice sales conversations with AI customers and receive coaching from an AI coach. This policy explains what personal data we collect, how we use it, and the choices you have.",
    ],
  },
  {
    title: "2. Data we collect",
    list: [
      ["Account data", "Your name, email address and a securely hashed password when you register."],
      ["Practice content", "The text of your roleplay and coaching conversations, the AI's replies, and the scores, feedback and training plans generated from them."],
      ["Voice data", "When you use voice features, short audio clips of your speech are captured and sent to our speech-to-text provider to be transcribed. Audio is processed transiently and is not stored by Pitch Craft; only the resulting transcript is kept as part of your conversation."],
      ["Progress & gamification data", "XP, levels, streaks, badges, win rates, lesson completions, challenge results, battle and tournament results."],
      ["Team data", "If you create or join a team: the team name, invite code, optional team logo and your membership, plus the practice statistics shown to your team manager."],
      ["Payment data", "If you subscribe, payments are processed by Stripe. We receive a subscription status and reference IDs — we never see or store your full card details."],
      ["Technical data", "Standard server logs (IP address, browser type, timestamps) used for security and reliability."],
    ],
  },
  {
    title: "3. How we use your data",
    list: [
      ["Provide the service", "Run roleplays, generate coaching, analyse conversations, build your personalised training plan, track progress, and power leaderboards, battles, tournaments and teams."],
      ["Personalise coaching", "Your past scores and weak spots are used so the AI coach and training plan adapt to you."],
      ["Team dashboards", "If you're in a team, your manager can see your streak, win rate, average score, weakest objection and when you last practiced."],
      ["Billing", "Manage subscriptions, trials and discount codes."],
      ["Security & improvement", "Detect abuse, fix bugs and improve the product. We do not sell your personal data."],
    ],
  },
  {
    title: "4. AI processing & third-party providers",
    body: [
      "To deliver the service we share the minimum necessary data with trusted processors:",
    ],
    list: [
      ["Large language model providers (e.g. Anthropic, OpenAI)", "Conversation text is sent to generate customer replies, coaching and analysis."],
      ["Speech providers (OpenAI Whisper & text-to-speech)", "Voice clips are transcribed and AI replies are converted to speech."],
      ["Stripe", "Subscription payments and checkout."],
      ["Hosting & database providers", "Secure storage and delivery of the app."],
    ],
    after: [
      "These providers process data on our behalf under their own security and privacy commitments. Your data is not used to train third-party foundation models where the provider offers that protection for API usage.",
    ],
  },
  {
    title: "5. Legal basis (UK & EU users)",
    body: [
      "We process your data to perform our contract with you (providing the service), for our legitimate interests (security, product improvement), to comply with legal obligations, and — for optional features such as voice and teams — on the basis of your consent, which you can withdraw at any time by not using those features or by deleting your account.",
    ],
  },
  {
    title: "6. Data retention",
    body: [
      "We keep your account and practice history while your account is active so that your coaching stays personalised. If you delete your account, we delete or anonymise your personal data within 30 days, except where we must retain records (for example, payment records) to meet legal obligations. Transient voice audio is discarded immediately after transcription.",
    ],
  },
  {
    title: "7. Your rights & deleting your account",
    body: [
      "Depending on where you live, you may have the right to access, correct, download, restrict or delete your personal data, to object to certain processing, and to lodge a complaint with your data-protection regulator (in the UK, the ICO). To exercise any of these rights, email us at the address below and we will respond within 30 days.",
      "You can delete your account and all associated data yourself at any time: sign in, open Account (bottom of the sidebar, or the Account tab on mobile) and choose \"Delete my account\". You'll be asked to re-enter your password. Your profile, conversations, scores, XP, badges, training plan and team membership are removed immediately and any active subscription is cancelled. If you can't sign in, email us and we'll delete it for you.",
      "You can also download a complete copy of your data at any time from Account → \"Download my data\", which gives you a machine-readable JSON file of your profile, conversations, scores, coaching history, plan, team and payment records.",
    ],
  },
  {
    title: "8. Security",
    body: [
      "Passwords are stored using strong one-way hashing, data is transmitted over encrypted connections (HTTPS), and access to production systems is restricted. No system is perfectly secure, so please use a unique password and let us know immediately if you suspect any unauthorised access.",
    ],
  },
  {
    title: "9. Children",
    body: [
      "Pitch Craft is a professional training tool intended for users aged 16 and over. We do not knowingly collect data from children. If you believe a child has created an account, please contact us and we will remove it.",
    ],
  },
  {
    title: "10. Permissions on mobile devices",
    body: [
      "If you install Pitch Craft as a mobile app, it may request access to your microphone (for voice practice) and network access. The microphone is only used while you are actively in a voice roleplay or coaching call and is never accessed in the background.",
    ],
  },
  {
    title: "11. Changes to this policy",
    body: [
      "We may update this policy from time to time. We will post the new version here and update the \"last updated\" date. Material changes will be highlighted in the app.",
    ],
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen grid-bg text-slate-900" style={{ background: "#F4F7FB" }} data-testid="privacy-page">
      <header className="max-w-3xl mx-auto px-6 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="privacy-home-link">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center glow-emerald"><Sparkles className="w-5 h-5 text-black" /></div>
          <span className="font-display font-extrabold">Pitch Craft</span>
        </Link>
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="privacy-back-link"><ArrowLeft className="w-4 h-4" /> Back</Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-emerald-600 font-mono text-xs uppercase tracking-widest">Legal</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">Privacy Policy</h1>
        <p className="text-slate-500 mt-3 text-sm">Last updated {UPDATED}</p>

        <div className="mt-10 space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.title} className="card-surface rounded-2xl p-6 sm:p-8" data-testid="privacy-section">
              <h2 className="text-base md:text-lg font-bold text-slate-900">{s.title}</h2>
              {(s.body || []).map((p, i) => <p key={`${i}-${p.slice(0, 24)}`} className="text-sm sm:text-base text-slate-600 mt-3 leading-relaxed">{p}</p>)}
              {s.list && (
                <ul className="mt-4 space-y-3">
                  {s.list.map(([k, v]) => (
                    <li key={k} className="text-sm sm:text-base text-slate-600 leading-relaxed flex gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span><span className="font-semibold text-slate-800">{k}:</span> {v}</span>
                    </li>
                  ))}
                </ul>
              )}
              {(s.after || []).map((p, i) => <p key={`${i}-${p.slice(0, 24)}`} className="text-sm sm:text-base text-slate-600 mt-4 leading-relaxed">{p}</p>)}
            </section>
          ))}

          <section className="card-surface rounded-2xl p-6 sm:p-8 border border-emerald-500/20" data-testid="privacy-contact">
            <h2 className="text-base md:text-lg font-bold text-slate-900">12. Contact us</h2>
            <p className="text-sm sm:text-base text-slate-600 mt-3 leading-relaxed">
              Questions, requests or concerns about your data? Email <a href={`mailto:${CONTACT}`} className="text-emerald-700 font-semibold hover:underline" data-testid="privacy-contact-email">{CONTACT}</a> and we'll get back to you within 30 days.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-600">
        © 2026 Pitch Craft · <Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link> · <Link to="/terms" className="hover:text-slate-900">Terms of Service</Link>
      </footer>
    </div>
  );
}
